/**
 * Bootstrap del SPA XOne generado.
 *
 * Este archivo es el unico autorizado a tocar `document`, `window` y `fetch`.
 * Los scripts XOne ejecutados dentro del runtime (`@xone-web/runtime-kernel`)
 * tienen prohibidos esos identificadores: ven solo `ui`, `self`, `appData`,
 * `$http`, `crypto`, `console`, `Bundle` y `ThisDataColl` (politica firme
 * documentada en `plans/javascript_library_implementation/README.md` §5b).
 *
 * Responsabilidades:
 *   1. Cargar manifiesto `/xone-manifest.json` (lo escribe el Generator Web).
 *   2. Cargar `default.css` (+ extras), `functions.js`, todos los `*.xne`.
 *      La base de datos NO se incluye en el SPA: el acceso a datos es
 *      online-only y va por HTTP contra `XoneBackendWeb`.
 *   3. Construir runtime + macros + storage remoto + http + crypto + ui +
 *      appData + eventBus + functions registry + xml renderer.
 *   4. Inyectar globales en el runtime y exponer `ui`, `appData`, etc.
 *   5. Cargar functions.js como include del runtime.
 *   6. Parsear y registrar todas las colecciones `<coll>`.
 *   7. Bootstrap navegacion: render del entry-point (`EntradaApp`).
 *
 * Plan: `plans/12_OnlineDataAccess/02_SpaLibs_OnlineOnly.md`.
 *
 * Politica async-first: todas las operaciones I/O usan `await`. Sin try/catch
 * globales — los errores tipados (`XoneScriptError`, `XoneAppDataError`)
 * propagan.
 */

import { createXoneRuntime, createConsole, type XoneRuntime } from '@xone-web/runtime-kernel';
import { GlobalMacroStore, bootstrapSystemMacros } from '@xone-web/macros';
import { createXoneEventBus, ScriptRegistry } from '@xone-web/events';
import { create$Http } from '@xone-web/http';
import { createCrypto, Bundle } from '@xone-web/crypto';
import { createAppData } from '@xone-web/appdata';
import { createUi } from '@xone-web/ui';
import type { XoneRouter, XoneViewHandle } from '@xone-web/ui';
import { createBackendClient } from '@xone-web/backend-client';
import { transformToStandardCss } from '@xone-web/css-engine';
import {
    BASELINE_CSS,
    parseMappings,
    parseXne,
    XmlRenderer,
    type XneColl,
    type XneFrame,
    type XneGroup,
    type XneProp,
} from '@xone-web/xml-renderer';
import {
    createDataCollection,
    createDataObject,
    createRemoteStorage,
    MemoryStorageAdapter,
    type DataCollection,
    type FieldDefinition,
    type XneCollDefinition,
} from '@xone-web/data';
import { createFunctionsRegistry } from '@xone-web/functions';

// ---------------------------------------------------------------------------
// Manifiesto generado por el Generator Web (lo escribe XoneSpaRenderer.cs).
// ---------------------------------------------------------------------------

interface XoneManifest {
    /** Nombre logico del proyecto (`app.ini` -> `Name`). */
    readonly name: string;
    /** Titulo amigable (`app.ini` -> `Title`). */
    readonly title: string;
    /** Prefijo de tablas (`app.xml` -> `<app prefix>`). */
    readonly tablePrefix: string;
    /** Idioma por defecto del sistema. */
    readonly defaultLanguage: string;
    /** Nombre de la coleccion entry-point (typicamente `EntradaApp`). */
    readonly entryPoint: string;
    /**
     * Nombre de la coleccion de login (`<login-coll>` en `app.xml`). `null` si
     * el proyecto no declara una login-coll personalizada — en ese caso el
     * bootstrap salta directo al entry-point.
     */
    readonly loginColl: string | null;
    /**
     * Atributo `autologon` de `<app>` en `app.xml`. Si `true`, el bootstrap
     * salta `<login-coll>` y va directo al `<entry-point>` (paridad con la
     * logica de `XoneAndroidFramework3.mainEntry`).
     */
    readonly autologon: boolean;
    /** Lista de archivos `.xne` a cargar. `mappings.xne` puede estar incluido. */
    readonly xneFiles: ReadonlyArray<string>;
    /** Lista de hojas CSS XOne a cargar. */
    readonly cssFiles: ReadonlyArray<string>;
    /** Lista de archivos JS a incluir (functions.js + extras). */
    readonly jsIncludes: ReadonlyArray<string>;
    /** URL base del backend XOne (puede ser ''). */
    readonly apiBase: string;
    /** Modo de acceso a datos. En web siempre `"online"`. */
    readonly mode: string;
}

// ---------------------------------------------------------------------------
// Helpers locales: unico lugar autorizado a usar `fetch`.
// ---------------------------------------------------------------------------

async function loadText(url: string): Promise<string> {
    const res = await fetch(url, { cache: 'no-cache' });
    if (!res.ok) {
        throw new Error(`No se pudo cargar ${url}: HTTP ${res.status}`);
    }
    return await res.text();
}

async function loadJson<T>(url: string): Promise<T> {
    const text = await loadText(url);
    return JSON.parse(text) as T;
}

/**
 * Carga un archivo XML respetando el encoding declarado en `<?xml encoding=".."?>`.
 *
 * Muchos proyectos XOne reales usan `iso-8859-15` o `iso-8859-1` en sus `.xne`
 * para preservar caracteres acentuados sin BOM UTF-8. Sin esta deteccion,
 * `fetch().text()` decodifica siempre como UTF-8 y produce mojibake (caracteres
 * como `�`).
 */
async function loadXml(url: string): Promise<string> {
    const res = await fetch(url, { cache: 'no-cache' });
    if (!res.ok) {
        throw new Error(`No se pudo cargar ${url}: HTTP ${res.status}`);
    }
    const bytes = new Uint8Array(await res.arrayBuffer());
    // Inspeccionar los primeros bytes como ASCII para leer la declaracion XML.
    const head = new TextDecoder('latin1').decode(
        bytes.subarray(0, Math.min(200, bytes.length)),
    );
    const m = /<\?xml[^?>]*encoding\s*=\s*["']([\w\-]+)["']/.exec(head);
    const charset = (m?.[1] ?? 'utf-8').toLowerCase();
    try {
        return new TextDecoder(charset, { fatal: false }).decode(bytes);
    } catch {
        // TextDecoder rechaza algunos labels exoticos. Fallback razonable.
        return new TextDecoder('utf-8').decode(bytes);
    }
}

// `tryLoadBytes` solo se usaba para cargar `bd/gestion.db` en el modo
// SQLite cliente. Eliminado al pasar el SPA a online-only.

/**
 * Obtiene o genera el DEVICE_ID del SPA (32 hex sin guiones, formato `ROWID`).
 *
 * - Persistente en `localStorage` para que el mismo navegador mantenga
 *   identidad entre recargas (paridad XOne mobile, donde el deviceId es
 *   estable durante toda la vida del dispositivo).
 * - Si `localStorage` no esta disponible (modo privado bloqueado),
 *   se genera uno volatil para esta sesion — el backend tolerara que el
 *   refresh-token se invalide.
 * - Generacion con `crypto.getRandomValues` (WebCrypto, no es el `crypto`
 *   XOne). Es el unico lugar autorizado a usar `window.crypto` —
 *   los scripts XOne no lo ven.
 */
function getOrCreateDeviceId(): string {
    const KEY = 'xone.device_id';
    try {
        const cached = localStorage.getItem(KEY);
        if (cached && /^[a-f0-9]{32}$/.test(cached)) return cached;
    } catch { /* localStorage bloqueado */ }
    const bytes = new Uint8Array(16);
    (window.crypto ?? globalThis.crypto).getRandomValues(bytes);
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    try { localStorage.setItem(KEY, hex); } catch { /* idem */ }
    return hex;
}

// ---------------------------------------------------------------------------
// Conversion AST .xne -> XneCollDefinition (subset operacional para @xone-web/data).
// ---------------------------------------------------------------------------

function extractFields(coll: XneColl): ReadonlyArray<FieldDefinition> {
    const out: FieldDefinition[] = [];
    const visitProp = (prop: XneProp): void => {
        if (!prop.name || !prop.type) return;
        const sizeAttr = prop.attrs['fieldsize'];
        const titleAttr = prop.attrs['title'] ?? prop.title;
        const visibility = typeof prop.visible === 'number' ? prop.visible : undefined;
        const field: FieldDefinition = {
            name: prop.name,
            type: prop.type,
            ...(sizeAttr !== undefined && { size: Number(sizeAttr) }),
            ...(titleAttr !== undefined && { title: titleAttr }),
            ...(visibility !== undefined && { visibility }),
            ...(prop.group !== undefined && { group: String(prop.group) }),
        };
        out.push(field);
    };
    const visitFrame = (frame: XneFrame): void => {
        for (const child of frame.children) {
            if (child.kind === 'prop') visitProp(child as XneProp);
            else if (child.kind === 'frame') visitFrame(child as XneFrame);
        }
    };
    const visitGroup = (group: XneGroup): void => {
        for (const prop of group.props) visitProp(prop);
        for (const frame of group.frames) visitFrame(frame);
    };
    for (const group of coll.groups) visitGroup(group);
    return out;
}

function astToDefinition(coll: XneColl, tablePrefix: string): XneCollDefinition {
    const baseTableName = (coll.attrs['updateobj'] ?? coll.attrs['objname'] ?? coll.name).trim();
    const sqlAttr = coll.attrs['sql'];
    const filterAttr = coll.attrs['filter'];
    const sortAttr = coll.attrs['sort'];
    const idFieldAttr = coll.attrs['idfield'];
    const specialAttr = coll.attrs['special'];
    const special = typeof specialAttr === 'string' &&
        specialAttr.toLowerCase() === 'true';

    // `_buildSelectSql` de @xone-web/data construye siempre
    // `SELECT * FROM <tablePrefix><tableName> WHERE ... ORDER BY ...`.
    // Cuando el `<coll>` declara un `sql` custom con alias `t1` (y posibles
    // JOINs para resolver MAP_* desde otras tablas) ese SELECT directo no
    // basta: pierde los `MAP_CATEGORIA_*` y, encima, los `defaultSort`/
    // `defaultFilter` referencian `t1.X` que no existe sin alias.
    //
    // Solución: cuando hay `sqlAttr`, envolvemos el SQL declarado como
    // SUBQUERY aliased `t1`. El SELECT generado queda:
    //   SELECT * FROM ( <sqlAttr-con-##PREF##-expandido> ) t1
    //   WHERE <filter referenciando t1.X>
    //   ORDER BY <sort referenciando t1.X>
    //
    // Como el SQL custom ya selecciona `t1.*` y los `MAP_*` derivados, el
    // outer SELECT * los hereda. `t1.X` en el WHERE/ORDER funciona porque
    // `t1` es el alias del subquery.
    //
    // Las macros `##PREF##` las desarrolla `_developMacros` antes de
    // executeSql, asi que dejamos los placeholders intactos aqui.
    let tableName: string;
    let effectivePrefix: string;
    if (sqlAttr && sqlAttr.trim().length > 0) {
        tableName = `(${sqlAttr.trim()}) t1`;
        effectivePrefix = ''; // ya esta dentro del sqlAttr
    } else {
        tableName = baseTableName;
        effectivePrefix = tablePrefix;
    }

    return {
        name: coll.name,
        tableName,
        tablePrefix: effectivePrefix,
        ...(idFieldAttr !== undefined && { idFieldName: idFieldAttr }),
        fields: extractFields(coll),
        ...(filterAttr !== undefined && { defaultFilter: filterAttr }),
        ...(sortAttr !== undefined && { defaultSort: sortAttr }),
        ...(coll.title !== undefined && { title: coll.title }),
        ...(sqlAttr !== undefined && { xmlNode: { sql: sqlAttr } }),
        ...(special && { special: true }),
    };
}

// ---------------------------------------------------------------------------
// Bootstrap principal
// ---------------------------------------------------------------------------

async function bootstrap(): Promise<void> {
    // 1) Manifiesto generado por el Generator Web.
    const manifest = await loadJson<XoneManifest>('/xone-manifest.json');

    // 2) CSS baseline + CSS XOne -> CSS estandar via @xone-web/css-engine.
    //
    // 2a) Baseline (@xone-web/xml-renderer): reset, defaults flex column de
    //     coll/group/frame, estilo de inputs nativos, floating label, password
    //     toggle. Se inyecta PRIMERO para que `default.css` del proyecto lo
    //     pueda sobreescribir mediante cascada estándar.
    const baseline = document.createElement('style');
    baseline.dataset['source'] = 'xone-baseline';
    baseline.textContent = BASELINE_CSS;
    document.head.appendChild(baseline);

    // 2b) CSS del proyecto: cada `<style url="...">` declarado en `app.xml`.
    for (const cssUrl of manifest.cssFiles) {
        const xoneCss = await loadText(`/${cssUrl}`);
        const standardCss = transformToStandardCss(xoneCss);
        const style = document.createElement('style');
        style.dataset['source'] = cssUrl;
        style.textContent = standardCss;
        document.head.appendChild(style);
    }

    // 3) Macros globales + bootstrap del subsistema de macros.
    //    `DEVICE_ID` se publica al arranque (paridad mobile) y NO se borra en
    //    logout. `API_BASE` apunta a la raiz del XoneBackendWeb (.NET): por
    //    defecto cadena vacia -> mismo origen + proxy `/api` declarado en
    //    `vite.config.ts`.
    const macros = new GlobalMacroStore();
    bootstrapSystemMacros(macros);
    macros.setGlobalMacro('APP_NAME', manifest.name);
    macros.setGlobalMacro('LANGUAGE', manifest.defaultLanguage);
    macros.setGlobalMacro('PREF', manifest.tablePrefix);
    macros.setGlobalMacro('DEVICE_ID', getOrCreateDeviceId());
    macros.setGlobalMacro('API_BASE', manifest.apiBase ?? '');
    macros.setGlobalMacro('APP_VERSION', '1.0.0');
    macros.setGlobalMacro('PLATFORM', 'Web');

    // 3b) Console adapter compartido (usado por storage init, runtime, router).
    //     Lo creamos pronto para poder logear warnings durante init de storage.
    const consoleAdapter = createConsole();

    // 4) Crypto + $http (necesarios para construir el BackendClient antes del
    //    storage remoto). El $http inyecta `Authorization: Bearer <TOKEN>`
    //    leyendo la macro `TOKEN` justo antes de cada request.
    const crypto = createCrypto();
    const $http = create$Http({
        macroReader: macros,
        defaultTimeoutMs: 30_000,
    });

    // 5) Cliente del backend XOne (`XoneBackendWeb`). Se construye ANTES del
    //    storage remoto porque el adapter lo consume. Si el manifest no fija
    //    `apiBase`, pasamos `'/'` para usar mismo origen + proxy de Vite.
    const backend = createBackendClient({
        apiBase: manifest.apiBase && manifest.apiBase.length > 0 ? manifest.apiBase : '/',
        $http,
        macros,
        autoIdempotency: true,
    });

    // 6) Storage REMOTO (online-only).
    //
    //    El SPA web no usa SQLite. Todas las operaciones de datos se traducen
    //    a llamadas HTTP contra XoneBackendWeb mediante el
    //    `XoneRemoteStorageAdapter` de `@xone-web/data`:
    //      - SELECT/WITH/PRAGMA/EXPLAIN  -> POST /api/v1/query/raw
    //        (o `backend.dataQuery.executeRaw` si el cliente lo expone).
    //      - INSERT/UPDATE/DELETE        -> warning + 0 filas afectadas;
    //        el CRUD remoto vive en /api/v1/dataobject/<coll>/<rowid?>
    //        (plan 12/02 §3).
    //    La factory `createRemoteStorage` devuelve un objeto con la forma
    //    `{ db: { executeSql, transaction, close } }` que `createAppData`
    //    consume. Plan: `plans/12_OnlineDataAccess/02_SpaLibs_OnlineOnly.md`.
    const remoteStorage = createRemoteStorage({
        backend,
        macros,
        apiBase: manifest.apiBase ?? '',
        logger: consoleAdapter,
    });
    const storage = remoteStorage as unknown as Parameters<typeof createAppData>[0]['storage'];
    consoleAdapter.info(`[storage] remote adapter activo (mode=${manifest.mode ?? 'online'})`);

    // 7) Event bus + registry de scripts.
    const bus = createXoneEventBus();
    const scriptRegistry = new ScriptRegistry();

    // 7) Runtime kernel (sandbox de scripts XOne).
    //    Como `ui` y `appData` se construyen DESPUES del runtime (para inyectar
    //    el propio runtime en `appData`), exponemos getters lazy. El runtime
    //    snapshotea el Map de globales tras buildGlobalsMap, asi que en su
    //    lugar usamos `extraGlobals` para nombres adicionales y resolvemos
    //    `ui`/`appData` mediante un proxy wrapper.
    const uiHolder: { ui: unknown } = { ui: null };
    const appDataHolder: { appData: unknown } = { appData: null };
    // `user` — DataObject del usuario autenticado (paridad
    // `XoneApplication.m_user` del framework Android). Se hidrata tras
    // `<onlogon>` con los campos devueltos por el backend y se publica
    // como global del runtime. Pre-login es `null`.
    const userHolder: { user: unknown } = { user: null };

    /**
     * Construye el DataObject `user` de la sesión a partir de los campos
     * devueltos por el endpoint `/api/v1/auth/login`. Réplica de
     * `XoneApplication.LogonUser` (Android), que asigna `m_user` a la fila
     * de Usuarios validada. Los nombres de campo siguen la convención XOne
     * (UPPER), accesibles como `user.NOMBRE`, `user.LOGIN`, etc.
     */
    function buildUserDataObject(fields: Record<string, unknown>): ReturnType<typeof createDataObject> {
        const tablePrefix = manifest.tablePrefix;
        const def: XneCollDefinition = collectionsDefs['Usuarios'] ?? {
            name: 'Usuarios',
            tableName: 'usuarios',
            tablePrefix,
            fields: [
                { name: 'ID', type: 'N' },
                { name: 'LOGIN', type: 'T' },
                { name: 'NOMBRE', type: 'T' },
                { name: 'ID_EMPRESA', type: 'N' },
                { name: 'ID_USUARIO', type: 'N' },
                { name: 'ROL', type: 'T' },
            ],
        };
        const coll = createDataCollection({
            name: def.name,
            definition: def,
            storage: new MemoryStorageAdapter(),
            macros,
            bus,
        });
        const initial: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(fields)) {
            // Convención XOne: nombres en UPPER. Si llega minúscula, lo subimos.
            initial[k.toUpperCase()] = v;
        }
        return createDataObject({ owner: coll, initial, isNew: false });
    }

    const lazyProxy = (target: { [k: string]: unknown }, key: string): unknown =>
        new Proxy({}, {
            get: (_t, p) => {
                const real = target[key] as Record<string | symbol, unknown> | null;
                if (!real) return undefined;
                const val = real[p as string];
                return typeof val === 'function' ? (val as () => unknown).bind(real) : val;
            },
        });

    const runtime: XoneRuntime = createXoneRuntime({
        globals: {
            ui: lazyProxy(uiHolder, 'ui'),
            appData: lazyProxy(appDataHolder, 'appData'),
            user: lazyProxy(userHolder, 'user'),
            $http,
            crypto,
            console: consoleAdapter,
            Bundle,
        },
        extraGlobals: [
            'self', 'This', 'ThisDataColl', 'Sender', 'Item', 'KeyCode',
            'deviceInfo', 'systemSettings', 'replica',
        ],
    });

    // 8) Functions registry para futuras invocaciones por nombre.
    const functions = createFunctionsRegistry();
    void functions;

    // 9) Cargar functions.js (+ extras) como includes del runtime.
    for (const jsPath of manifest.jsIncludes) {
        const source = await loadText(`/${jsPath}`);
        runtime.loadIncludeFile(jsPath, source);
    }

    // 10) Parsear todas las `*.xne` y construir mapas { name -> ast / def }.
    //
    // `mappings.xne` tiene formato canonico distinto (estructura xml/app/collprops/coll
    // segun docs/xone-help-docs/topics/01-xone-fundamentals.md §4.3) y contiene SOLO
    // las colecciones base `Empresas` y `Usuarios`. Lo detectamos por nombre y usamos
    // `parseMappings`. El resto de .xne va por `parseXne` (un <coll> raiz cada uno).
    const collectionAsts = new Map<string, XneColl>();
    const collectionsDefs: Record<string, XneCollDefinition> = {};
    const isMappingsFile = (path: string): boolean => /(^|\/)mappings\.xne$/i.test(path);

    for (const xnePath of manifest.xneFiles) {
        const xml = await loadXml(`/${xnePath}`);
        try {
            if (isMappingsFile(xnePath)) {
                const { app, collections } = parseMappings(xml);
                // El prefijo del manifest manda, pero si `mappings.xne` declara
                // un prefix distinto avisamos: ambos deben coincidir (regla XOne).
                if (app && app.prefix && app.prefix !== manifest.tablePrefix) {
                    consoleAdapter.warn(
                        `[bootstrap] mappings.xne declara prefix='${app.prefix}' pero ` +
                            `el manifest indica '${manifest.tablePrefix}'. Se usa el del manifest.`,
                    );
                }
                for (const ast of collections) {
                    collectionAsts.set(ast.name, ast);
                    collectionsDefs[ast.name] = astToDefinition(ast, manifest.tablePrefix);
                }
                continue;
            }
            const ast = parseXne(xml);
            collectionAsts.set(ast.name, ast);
            collectionsDefs[ast.name] = astToDefinition(ast, manifest.tablePrefix);
        } catch (err) {
            consoleAdapter.warn(`[bootstrap] Saltando ${xnePath}: ${(err as Error).message}`);
        }
    }

    // 11) Construir XmlRenderer (singleton). El router lo reusa: cada navegacion
    //     destruye el estado anterior y re-renderiza el AST de la nueva colección.
    //
    //     Inyectamos `astResolver` y `contentsResolver` para que el renderer
    //     pueda inflar `<prop type="Z">` (gridview/recyclerview) sin depender
    //     directamente de `appData` (mantenemos `xml-renderer` agnostico):
    //
    //       - astResolver: lookup en el Map `collectionAsts` (ya poblado por el
    //         parser arriba).
    //       - contentsResolver: pide a `appData.getCollection(src, true)` los
    //         DataObjects de la coleccion destino (loadAll). El filtro/sort
    //         declarados en el `<contents>` se pasan adelante; appData los
    //         aplica vía SQL.
    //
    //     Esto se construye DESPUES de `appData` (paso 15), asi que aqui solo
    //     pasamos closures que se resuelven lazy.
    let appDataRef: { current: unknown } = { current: null };
    // DataObject de la pantalla actualmente renderizada. Lo setea
    // `renderCollectionByName` (paso 12) y lo consume el `contentsResolver`
    // de abajo para que las macros `##FLD_X##` / `##OWN_X##` en
    // `<contents filter="..." sort="...">` se resuelvan contra él, no contra
    // las filas de la coleccion hija. Réplica de
    // `XoneDataObject.ReplaceFieldValueMacros` del framework Android.
    const currentScreen: { dataObject: unknown } = { dataObject: null };
    const renderer = new XmlRenderer({
        bus,
        registry: scriptRegistry,
        runtime,
        astResolver: (name) => collectionAsts.get(name),
        contentsResolver: async (src, opts) => {
            const ad = appDataRef.current as
                | {
                      getCollection: (n: string, load: true) => Promise<unknown>;
                  }
                | null;
            if (!ad) return [];
            const coll = await ad.getCollection(src, true) as
                | {
                      getObjects?: () => ReadonlyArray<unknown>;
                      getCount?: () => number;
                      get?: (i: number) => unknown;
                      setFilter?: (s: string) => void;
                      getFilter?: () => string;
                      setSort?: (s: string) => void;
                      reload?: () => Promise<boolean>;
                      setFieldMacroOwner?: (owner: { getValue: (f: string) => unknown } | null) => void;
                  }
                | null;
            if (!coll) return [];

            // Inyectar el DataObject de la pantalla actual como owner de las
            // macros ##FLD_/##OWN_ que aparezcan en el filter/sort de la
            // colección hija. Sin esto, `t1.IDCATEGORIA=##FLD_FILTRO_IDCAT##`
            // se quedaría sin resolver y el backend devolvería un set vacío.
            if (typeof coll.setFieldMacroOwner === 'function') {
                coll.setFieldMacroOwner(
                    currentScreen.dataObject as { getValue: (f: string) => unknown } | null,
                );
            }

            // Aplicar filter/sort declarados en el `<contents ... filter=
            // "t1.DESTACADA=1" sort="...">`. La colección puede estar
            // cacheada con un filter previo, asi que comparamos antes de
            // forzar reload (un reload extra equivale a otro round-trip SQL).
            const desiredFilter = opts.filter ?? '';
            const currentFilter = typeof coll.getFilter === 'function' ? coll.getFilter() : '';
            let needsReload = false;
            if (desiredFilter !== currentFilter && typeof coll.setFilter === 'function') {
                coll.setFilter(desiredFilter);
                needsReload = true;
            }
            if (opts.sort && typeof coll.setSort === 'function') {
                coll.setSort(opts.sort);
                needsReload = true;
            }
            if (needsReload && typeof coll.reload === 'function') {
                await coll.reload();
            }

            // Iterar items. `getObjects()` es la API moderna; si no existe,
            // fallback al patron índice/get.
            const fnObjects = coll.getObjects;
            if (typeof fnObjects === 'function') {
                return fnObjects.call(coll) as ReadonlyArray<import('@xone-web/data').DataObject>;
            }
            const fnGet = coll.get;
            const count = typeof coll.getCount === 'function' ? coll.getCount() : 0;
            const out: unknown[] = [];
            if (typeof fnGet === 'function') {
                for (let i = 0; i < count; i += 1) out.push(fnGet.call(coll, i));
            }
            return out as ReadonlyArray<import('@xone-web/data').DataObject>;
        },
    });

    const root = document.getElementById('app');
    if (!root) {
        throw new Error('Bootstrap: no se encontro el contenedor #app en index.html');
    }

    // 12) Router del SPA. Implementa `XoneRouter` para que `ui.openMenu()` /
    //     `ui.openEditView()` reemplacen el contenido de `#app` con la nueva
    //     coleccion. El fallback de `@xone-web/ui` solo mantiene un stack en
    //     memoria sin tocar el DOM; aqui sustituimos por el router real.
    //
    //     Paridad XOne:
    //       - `render()` del XmlRenderer ya dispara `create -> load` automatico.
    //       - El framework Android tambien dispara `<before-edit>` y
    //         `<after-edit>` al abrir una coleccion en modo edicion; los
    //         disparamos aqui mediante `renderer.runEvent('before-edit')`
    //         y `runEvent('after-edit')` para que el script de inicializacion
    //         de la pantalla (cargar contadores, saludos, etc.) se ejecute.
    const viewStack: XoneViewHandle[] = [];
    let viewCounter = 0;

    async function renderCollectionByName(
        collectionName: string,
        target: unknown,
        params: Record<string, unknown>,
    ): Promise<void> {
        const ast = collectionAsts.get(collectionName);
        if (!ast) {
            throw new Error(
                `Router: coleccion '${collectionName}' no esta en xneFiles. ` +
                `Disponibles: ${Array.from(collectionAsts.keys()).join(', ')}`,
            );
        }

        // Cada navegacion arranca con DOM/listeners limpios.
        renderer.destroy();
        root!.replaceChildren();

        // DataObject de la pantalla. En esta fase usamos siempre memoria
        // (`MemoryStorageAdapter`); la persistencia SQL real es trabajo
        // posterior. Si `target` ya es un DataObject (caso `openEditView`),
        // lo reutilizamos; en caso contrario creamos uno nuevo vacio.
        let dataObj: ReturnType<typeof createDataObject>;
        if (target && typeof target === 'object' && 'getValue' in (target as object)) {
            dataObj = target as ReturnType<typeof createDataObject>;
        } else {
            const def: XneCollDefinition = collectionsDefs[collectionName] ?? {
                name: collectionName,
                tableName: collectionName,
                tablePrefix: manifest.tablePrefix,
                fields: [],
            };
            const memAdapter = new MemoryStorageAdapter();
            const coll: DataCollection = createDataCollection({
                name: def.name,
                definition: def,
                storage: memAdapter,
                macros,
                bus,
            });
            const initial: Record<string, unknown> = {};
            for (const [k, v] of Object.entries(params)) initial[k] = v;
            dataObj = createDataObject({ owner: coll, initial, isNew: true });
        }

        // Publicar el DataObject de la pantalla como owner contextual para
        // las macros ##FLD_X## / ##OWN_X## que aparezcan en `<contents>`
        // hijos de esta pantalla (réplica framework Android).
        currentScreen.dataObject = dataObj;

        // 1) render dispara create -> load.
        await renderer.render(ast, root!, dataObj);
        // 2) before-edit / after-edit: scripts de inicializacion (carga de
        //    contadores, saludos, etc.). Si la coleccion no declara estos
        //    eventos, `runEvent` retorna un resultado vacio sin error.
        try {
            await renderer.runEvent('before-edit');
        } catch (err) {
            consoleAdapter.warn(`[router] before-edit fallo en '${collectionName}':`, err);
        }
        // Tras before-edit los scripts XOne típicamente escriben en self.MAP_*
        // (saludos dinamicos, contadores, valores derivados). Los controles
        // display-only (span.xone-text-value) no observan automaticamente el
        // DataObject, asi que disparamos un refreshAll para que reflejen los
        // valores recien calculados antes de mostrar la pantalla al usuario.
        // Paridad mobile: tras `<before-edit>`, EditViewHyper llama a
        // `EditGroupView.refreshFloatingProperties()` para repintar.
        try {
            await renderer.refreshAll();
        } catch (err) {
            consoleAdapter.warn(`[router] refreshAll post before-edit fallo:`, err);
        }
        try {
            await renderer.runEvent('after-edit');
        } catch (err) {
            consoleAdapter.warn(`[router] after-edit fallo en '${collectionName}':`, err);
        }
        // Inflar `<prop type="Z">` (contents Z): lookup de la coleccion destino
        // y render recursivo de items en gridview/recyclerview. Se hace ANTES
        // de devolver el control al usuario para que la pantalla se vea
        // completa (incluyendo categorias/recetas/etc) al primer paint.
        try {
            await renderer.inflateContents();
        } catch (err) {
            consoleAdapter.warn(`[router] inflateContents fallo en '${collectionName}':`, err);
        }
    }

    const router: XoneRouter = {
        async openMenu(collection, params) {
            viewCounter += 1;
            viewStack.length = 0;
            viewStack.push({
                id: `view-${viewCounter}-${collection}`,
                collection,
                target: null,
                params: { ...(params ?? {}) },
            });
            await renderCollectionByName(collection, null, params ?? {});
        },
        async openEditView(target, params) {
            viewCounter += 1;
            // XOne mobile (Rhino) trata `createObject(...)` como SYNC y el
            // script idiomatico es:
            //   ui.openEditView(appData.getCollection("X").createObject({...}, true));
            // En el SPA web `createObject` es async (devuelve Promise<DataObject>).
            // Sin tocar los scripts XOne legacy, await-eamos la promesa aqui si
            // el target lo es.
            let resolved: unknown = target;
            if (target && typeof target === 'object' && typeof (target as { then?: unknown }).then === 'function') {
                try {
                    resolved = await (target as Promise<unknown>);
                } catch (err) {
                    consoleAdapter.warn('[router] openEditView: target Promise rechazada:', err);
                    return;
                }
            }
            // Resolución del nombre de colección. Tras await el target ya es un
            // DataObject (o string, o algo más). Obtenemos el nombre via
            // `dataObject.getOwnerCollection().getName()`. Si el callsite pasa
            // un string, se interpreta como nombre directo.
            let name = 'detail';
            if (typeof resolved === 'string') {
                name = resolved;
            } else if (resolved && typeof resolved === 'object') {
                const t = resolved as {
                    getOwnerCollection?: () => { getName?: () => string };
                    getCollectionName?: () => string;
                };
                if (typeof t.getOwnerCollection === 'function') {
                    const coll = t.getOwnerCollection();
                    if (coll && typeof coll.getName === 'function') {
                        name = coll.getName();
                    }
                } else if (typeof t.getCollectionName === 'function') {
                    name = t.getCollectionName();
                }
            }
            viewStack.push({
                id: `view-${viewCounter}-${name}`,
                collection: name,
                target: resolved,
                params: { ...(params ?? {}) },
            });
            await renderCollectionByName(name, resolved, params ?? {});
        },
        async returnToMainMenu() {
            if (viewStack.length <= 1) return;
            const root0 = viewStack[0]!;
            viewStack.length = 1;
            await renderCollectionByName(root0.collection, root0.target, { ...root0.params });
        },
        /**
         * Pop del top del stack y re-render del anterior. Equivalente al
         * gesto "atrás" del sistema. Lo invoca `ui.getView(self).exit()` y
         * el handler nativo back de las cabeceras XOne.
         */
        async goBack() {
            if (viewStack.length <= 1) return;
            viewStack.pop();
            const prev = viewStack[viewStack.length - 1]!;
            await renderCollectionByName(prev.collection, prev.target, { ...prev.params });
        },
        getStack() {
            return viewStack.slice();
        },
        getCurrent() {
            return viewStack.length > 0 ? viewStack[viewStack.length - 1]! : null;
        },
    };

    // 13) Construir `ui` (web components nativos, sin framework).
    const dialogPortal = document.getElementById('xone-dialog-portal') ?? undefined;
    const ui = createUi({
        router,
        ...(dialogPortal !== undefined && { dialogPortal }),
        language: manifest.defaultLanguage,
    });
    uiHolder.ui = ui;

    // 14) Adapter del cliente backend para `appData` (XoneApplication).
    //
    //     `@xone-web/backend-client` envuelve los 11 servicios del backend.
    //     `appData` (que es agnostico de la implementacion HTTP) solo conoce
    //     un subset `IBackendClient` minimal: aqui construimos un ADAPTER que
    //     traduce entre la firma `IBackendAuth { login(user,password) }` que
    //     espera `createAppData` y la firma real del backend
    //     `LoginRequest { USUARIO, PASSWORD, DEVICE_ID, PLATFORM, APP_VERSION }`.
    //
    //     El `backend` se construyo en el paso 5 (antes del storage remoto).
    //     Esto desacopla `createAppData` (publicado como libreria) del shape
    //     concreto de los DTOs del backend; si cambia el contrato del .NET,
    //     solo se toca este adapter.
    const deviceId = macros.getGlobalMacro('DEVICE_ID') ?? '';
    const appVersion = macros.getGlobalMacro('APP_VERSION') ?? '1.0.0';
    const backendClientAdapter = {
        auth: {
            async login(input: {
                user: string;
                password: string;
                enterprise?: string | number;
                deviceId?: string;
            }) {
                const r = await backend.auth.login({
                    USUARIO: input.user,
                    PASSWORD: input.password,
                    DEVICE_ID: input.deviceId ?? deviceId,
                    PLATFORM: 'Web',
                    APP_VERSION: appVersion,
                    ...(typeof input.enterprise === 'number'
                        ? { ID_EMPRESA: input.enterprise }
                        : {}),
                });
                // Publicamos MACROS_SESION del backend ANTES de que appData
                // procese el resultado; asi las macros de sesion estan vivas
                // al disparar `<onlogon>`.
                if (r.MACROS_SESION) {
                    for (const [k, v] of Object.entries(r.MACROS_SESION)) {
                        macros.setGlobalMacro(k, v);
                    }
                }
                // Hidratar `user` con la fila Usuarios devuelta por el backend.
                // Paridad `XoneApplication.LogonUser` Android (m_user). Aún no
                // hacemos un GET adicional a `Usuarios/{rowid}`: lo que devuelve
                // el endpoint de auth ya cubre `user.NOMBRE`, `user.LOGIN`,
                // `user.ID_USUARIO`, `user.ID_EMPRESA`, `user.ROL`. Para campos
                // adicionales (foto, idioma, MAP_*) habrá que invocar
                // `/api/v1/dataobject/Usuarios/{rowid}` en una fase posterior.
                userHolder.user = buildUserDataObject({
                    LOGIN: input.user,
                    NOMBRE: r.NOMBRE ?? input.user,
                    ID_USUARIO: r.ID_USUARIO,
                    ID_EMPRESA: r.ID_EMPRESA,
                    ROL: r.ROL,
                });
                return {
                    token: r.ACCESS_TOKEN,
                    refreshToken: r.REFRESH_TOKEN,
                    userId: r.ID_USUARIO,
                    enterpriseId: r.ID_EMPRESA,
                    rol: r.ROL,
                    expiresIn: r.EXPIRES_IN,
                };
            },
            async logout() {
                const refresh = macros.getGlobalMacro('REFRESH_TOKEN') ?? '';
                userHolder.user = null;
                if (!refresh) return;
                try {
                    await backend.auth.logout({
                        REFRESH_TOKEN: refresh,
                        DEVICE_ID: deviceId,
                    });
                } catch (err) {
                    consoleAdapter.warn('[backend] logout fallo:', err);
                }
            },
        },
    };

    // 15) Construir `appData` (XoneApplication) inyectando todo lo de arriba.
    const appData = createAppData({
        appName: manifest.name,
        tablePrefix: manifest.tablePrefix,
        defaultLanguage: manifest.defaultLanguage,
        appPath: '/',
        filesPath: '/files',
        macros,
        bus,
        runtime,
        storage,
        crypto,
        $http,
        collectionsDefinitions: collectionsDefs,
        backendClient: backendClientAdapter,
        onExit: () => { window.location.href = '/'; },
        onRestart: () => { window.location.reload(); },
    });
    appDataHolder.appData = appData;
    // Resolver `appDataRef` que captura el contentsResolver del XmlRenderer:
    // ahora que appData existe, el renderer ya puede cargar colecciones para
    // inflar `<prop type="Z">` durante el render de cada pantalla.
    appDataRef.current = appData;

    // 16) Navegacion post-login: el framework Android, tras `appData.login`,
    //     abre la coleccion declarada en `<entry-point>`. En el SPA web el
    //     equivalente se obtiene suscribiendose al evento de coleccion
    //     `onlogon` que emite `appData` tras un login exitoso. Asi:
    //       - el script XOne del LoginColl puede limitarse a llamar
    //         `appData.login({...})` sin preocuparse por la navegacion;
    //       - el callback `onLoginSuccessful` del script se ejecuta antes
    //         de la navegacion (UI like toast "Bienvenido");
    //       - este bootstrap garantiza la transicion al entry-point.
    bus.on('onlogon', async () => {
        await router.openMenu(manifest.entryPoint);
    });

    // 17) Decision de primera pantalla (paridad XOne):
    //
    //   - Si `autologon === true` o no hay `<login-coll>` declarada
    //       -> render directo del `<entry-point>`.
    //   - Si `autologon === false` y existe `loginColl` cargada
    //       -> render de la `<login-coll>` (el script del boton "Entrar"
    //          llamara a `appData.login()` y, tras exito, debera invocar
    //          `ui.openMenu(<entry-point>)`).
    //
    //   Validamos contra `collectionAsts` para que un manifest con un
    //   `loginColl` mal escrito no rompa el arranque: en ese caso degradamos
    //   al entry-point con un warning.
    const wantsLogin = !manifest.autologon && !!manifest.loginColl;
    const loginAvailable = wantsLogin && collectionAsts.has(manifest.loginColl!);
    if (wantsLogin && !loginAvailable) {
        consoleAdapter.warn(
            `[bootstrap] login-coll '${manifest.loginColl}' declarada en manifest pero ` +
                `no presente en xneFiles. Saltando al entry-point.`,
        );
    }
    const firstColl = loginAvailable ? manifest.loginColl! : manifest.entryPoint;
    if (!collectionAsts.has(firstColl)) {
        throw new Error(
            `Bootstrap: primera coleccion '${firstColl}' no esta en xneFiles ` +
                `(autologon=${manifest.autologon}, loginColl=${manifest.loginColl ?? '<null>'}).`,
        );
    }
    await router.openMenu(firstColl);

    // Exponer handles utiles en `window` para debug — los scripts XOne NO los ven.
    (window as unknown as Record<string, unknown>).__xone = {
        manifest, runtime, macros, bus, appData, ui, $http, crypto, storage,
        renderer, router, scriptRegistry, collectionAsts, collectionsDefs,
        firstColl,
    };
}

// ---------------------------------------------------------------------------
// Arranque + handler global de errores (UI minima de diagnostico).
// ---------------------------------------------------------------------------

bootstrap().catch((err: unknown) => {
    const message = err instanceof Error ? (err.stack ?? err.message) : String(err);
    const pre = document.createElement('pre');
    pre.style.cssText = 'font-family:monospace;color:#b00020;padding:24px;white-space:pre-wrap;background:#fff3f3;';
    pre.textContent = message;
    document.body.appendChild(pre);
});
